import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import Navbar from '../Navbar';
import Footer from '../Footer';

const CapabilitiesPage = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      hero: {
        title: 'Production Capabilities',
        subtitle: 'One integrated facility. Every stage from graphic preparation to finished shipment runs under our roof in Istanbul.'
      },
      s01: {
        number: '01',
        title: 'Product Lines',
        cards: [
          { title: 'Woven Labels', desc: 'High-density damask and satin labels with up to 8 colours, woven on Swiss-made Jakob Müller looms.' },
          { title: 'Printed Labels', desc: 'Flexographic, rotary screen and digital printing on satin, taffeta, nylon and recycled substrates.' },
          { title: 'Hang Tags', desc: 'FSC-certified cardstock from 250 to 450 gsm with foil stamping, embossing, spot UV and custom die-cut shapes.' },
          { title: 'Patches', desc: 'Embroidered, woven, leather, PVC and silicone patches with iron-on, sew-on and Velcro backing options.' },
          { title: 'Heat Transfers', desc: 'Tagless branding solution printed and die-cut in-house for direct application onto garments.' },
          { title: 'Care Labels', desc: 'ISO 3758 compliant wash-care and composition labels printed on soft-hand substrates for comfort.' }
        ]
      },
      s02: {
        number: '02',
        title: 'Equipment & Technology',
        items: [
          { title: 'Jakob Müller Looms (Switzerland)', desc: 'Our weaving department runs on Jakob Müller narrow-fabric looms, the industry standard for high-definition woven labels. Capable of producing labels with up to 12 thread colours and ultra-fine detail in both damask and satin constructions.' },
          { title: 'Four printing technologies', desc: 'Flexographic, digital, rotary screen and offset printing (Heidelberg) under one roof. Substrate and finish determine which technology is selected — no single-method limitation, no outsourcing.' },
          { title: 'Cutting & finishing lines', desc: 'Ultrasonic slitting, laser cutting, hot knife and die-cut stations handle all fold types — end fold, centre fold, mitre fold, Manhattan fold and book cover. Finishing includes heat-seal coating and adhesive backing.' },
          { title: 'In-house CAD & graphic studio', desc: 'Dedicated pre-press and graphic preparation team. Artwork files are optimised for production internally before any job reaches the floor, reducing revision cycles and lead time.' }
        ]
      },
      s03: {
        number: '03',
        title: 'Materials & Finishes',
        columns: [
          {
            title: 'Substrates',
            items: ['Satin', 'Damask', 'Taffeta', 'Nylon', 'Recycled polyester', 'Organic cotton', 'FSC-certified paper (250–450 gsm)', 'Kraft board']
          },
          {
            title: 'Finishing',
            items: ['Hot foil stamping', 'Embossing / debossing', 'Spot UV', 'Soft-touch lamination', 'Matte / gloss lamination', 'Heat-seal backing']
          },
          {
            title: 'Colour',
            items: ['Pantone matching', 'CMYK process', 'Up to 8-colour jacquard weave', 'Metallic and holographic foils']
          }
        ]
      },
      s04: {
        number: '04',
        title: 'Quality Control',
        items: [
          { title: 'Inline inspection', desc: 'Inline inspection on every finishing line. No production starts without approved samples. Every order is inspected before packing. Sample approval shipped to client before bulk production begins.' }
        ]
      },
      s05: {
        title: 'Certifications',
        certs: ['OEKO-TEX Standard 100', 'GRS', 'OEKO-TEX Recycled', 'FSC', 'RCS', 'PETA-Approved Vegan', 'SEDEX'],
        certImages: [
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/z6os3fp8_sdadadsdadas.png',
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/rd43kxco_Global%20Recycled%20Standard%20%28GRS%29%20%E2%80%94%20Recycled%20content%20with%20social%20and%20environmental%20criteria.png',
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/b1kii4km_OEKO-TEX%C2%AE%20Recycled%20%E2%80%94%20Certified%20for%20recycled%20textile%20products.png',
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/uhrbtqt7_FSC%C2%AE%20%E2%80%94%20Responsibly%20sourced%20paper%20and%20board.png',
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/qz6kus10_Recycled%20Claim%20Standard%20%28RCS%29%20%E2%80%94%20Recycled%20content%20traceability.png',
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/rt486shs_PETA%20Approved%20Vegan%20%E2%80%94%20No%20animal-derived%20materials.png',
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/91jzm374_SEDEX%20-%20SMETA%20%E2%80%94%20Audit-ready%20social%20compliance.png'
        ]
      },
      cta: {
        title: 'See it before you spec it',
        subtitle: 'Get a physical sample pack to feel our material and print quality before finalizing your order.',
        button: 'Request Sample Pack'
      }
    },
    tr: {
      hero: {
        title: 'Üretim Yetenekleri',
        subtitle: 'Tek entegre tesis. Grafik hazırlıktan bitmiş sevkiyata kadar her aşama İstanbul\'daki çatımız altında gerçekleşir.'
      },
      s01: {
        number: '01',
        title: 'Ürün Hatları',
        cards: [
          { title: 'Dokuma Etiketler', desc: 'İsviçre yapımı Jakob Müller tezgahlarında 8 renge kadar dokunabilen yüksek yoğunluklu damask ve saten etiketler.' },
          { title: 'Baskılı Etiketler', desc: 'Saten, tafta, naylon ve geri dönüştürülmüş substratlarda fleksografik, rotary serigrafi ve dijital baskı.' },
          { title: 'Karton Etiketler', desc: '250–450 gsm FSC sertifikalı kartonda folyo baskı, kabartma, spot UV ve özel kesim şekillerle üretim.' },
          { title: 'Yamalar', desc: 'Nakış, dokuma, deri, PVC ve silikon yamalar — ütüyle yapışan, dikişle takılan ve cırt cırt seçenekleriyle.' },
          { title: 'Isı Transferler', desc: 'Giysiye doğrudan uygulama için bünyemizde basılan ve kesilen etiketsiz markalama çözümü.' },
          { title: 'Bakım Etiketleri', desc: 'Konfor için yumuşak tuşeli substratlara basılan ISO 3758 uyumlu yıkama bakım ve kompozisyon etiketleri.' }
        ]
      },
      s02: {
        number: '02',
        title: 'Ekipman & Teknoloji',
        items: [
          { title: 'Jakob Müller Tezgahları (İsviçre)', desc: 'Dokuma departmanımız, yüksek çözünürlüklü dokuma etiketler için endüstri standardı olan Jakob Müller dar kumaş tezgahlarıyla çalışır. Damask ve saten yapılarda 12 iplik rengine kadar ultra ince detay üretme kapasitesine sahiptir.' },
          { title: 'Dört baskı teknolojisi', desc: 'Fleksografik, dijital, rotary serigrafi ve ofset baskı (Heidelberg) tek çatı altında. Substrat ve son işlem hangi teknolojinin seçileceğini belirler — tek yöntem sınırlaması yok, dış kaynak kullanımı yok.' },
          { title: 'Kesim & son işlem hatları', desc: 'Ultrasonik kesim, lazer kesim, sıcak bıçak ve kalıp kesim istasyonları tüm katlama tiplerini gerçekleştirir — uç katlama, orta katlama, gönye katlama, Manhattan katlama ve kitap kapağı. Son işlem ısı mühür kaplama ve yapışkanlı sırt içerir.' },
          { title: 'Bünyemizdeki CAD & grafik stüdyosu', desc: 'Özel baskı öncesi ve grafik hazırlık ekibi. Sanat dosyaları, herhangi bir iş üretime geçmeden önce dahili olarak optimize edilerek revizyon döngülerini ve teslim süresini azaltır.' }
        ]
      },
      s03: {
        number: '03',
        title: 'Malzemeler & Son İşlemler',
        columns: [
          {
            title: 'Substratlar',
            items: ['Saten', 'Damask', 'Tafta', 'Naylon', 'Geri dönüştürülmüş polyester', 'Organik pamuk', 'FSC sertifikalı kağıt (250–450 gsm)', 'Kraft karton']
          },
          {
            title: 'Son İşlemler',
            items: ['Sıcak folyo baskı', 'Kabartma / çökertme', 'Spot UV', 'Yumuşak dokunuş laminasyon', 'Mat / parlak laminasyon', 'Isı mühür sırt']
          },
          {
            title: 'Renk',
            items: ['Pantone eşleştirme', 'CMYK proses', '8 renge kadar jakarlı dokuma', 'Metalik ve holografik folyolar']
          }
        ]
      },
      s04: {
        number: '04',
        title: 'Kalite Kontrol',
        items: [
          { title: 'Inline kontrol', desc: 'Her son işlem hattında inline kontrol. Onaylı numune olmadan üretime başlanmaz. Her sipariş paketleme öncesi kontrol edilir. Seri üretime geçmeden önce numune onayı müşteriye gönderilir.' }
        ]
      },
      s05: {
        title: 'Sertifikalar',
        certs: ['OEKO-TEX Standard 100', 'GRS', 'OEKO-TEX Recycled', 'FSC', 'RCS', 'PETA Onaylı Vegan', 'SEDEX'],
        certImages: [
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/z6os3fp8_sdadadsdadas.png',
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/rd43kxco_Global%20Recycled%20Standard%20%28GRS%29%20%E2%80%94%20Recycled%20content%20with%20social%20and%20environmental%20criteria.png',
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/b1kii4km_OEKO-TEX%C2%AE%20Recycled%20%E2%80%94%20Certified%20for%20recycled%20textile%20products.png',
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/uhrbtqt7_FSC%C2%AE%20%E2%80%94%20Responsibly%20sourced%20paper%20and%20board.png',
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/qz6kus10_Recycled%20Claim%20Standard%20%28RCS%29%20%E2%80%94%20Recycled%20content%20traceability.png',
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/rt486shs_PETA%20Approved%20Vegan%20%E2%80%94%20No%20animal-derived%20materials.png',
          'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/91jzm374_SEDEX%20-%20SMETA%20%E2%80%94%20Audit-ready%20social%20compliance.png'
        ]
      },
      cta: {
        title: 'Belirlemeden önce görün',
        subtitle: 'Siparişinizi kesinleştirmeden önce malzeme ve baskı kalitemizi hissetmek için fiziksel numune seti alın.',
        button: 'Numune Seti Talep Edin'
      }
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-white" data-testid="capabilities-page">
      <Navbar />
      
      {/* Hero */}
      <section className="relative min-h-[350px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/j2wu35i4_sadasf.png" alt="Etiteks label production facility in Istanbul" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 pb-12 pt-32">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight" data-testid="capabilities-title">
            {t.hero.title}
          </h1>
          <p className="text-lg text-gray-200 max-w-3xl leading-relaxed">
            {t.hero.subtitle}
          </p>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-16 border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10">How We Work</h2>
          <img 
            src="https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/t2dboh8l_WhatsApp%20Image%202026-05-25%20at%2018.32.13.jpeg" 
            alt="How We Work — 1. Listen, 2. Sample, 3. Revise, 4. Produce, 5. Deliver" 
            className="w-full mx-auto"
          />
        </div>
      </section>

      {/* 01 — Product Lines */}
      <section className="py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-[1fr] items-start">
            
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">{t.s01.title}</h2>
              <div className="space-y-5">
                {t.s01.cards.map((card, i) => (
                  <div key={i} className="flex gap-4 items-baseline border-b border-gray-100 pb-5 last:border-0 last:pb-0">
                    <h3 className="text-base font-semibold text-gray-900 whitespace-nowrap min-w-[140px]">{card.title}</h3>
                    <p className="text-sm text-gray-500">{card.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 02 — Equipment & Technology */}
      <section className="py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-[1fr] items-start">
            
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">{t.s02.title}</h2>
              <div className="space-y-8">
                {t.s02.items.map((item, i) => (
                  <div key={i}>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 03 — Materials & Finishes */}
      <section className="py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-[1fr] items-start">
            
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">{t.s03.title}</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {t.s03.columns.map((col, i) => (
                  <div key={i}>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">{col.title}</h3>
                    <ul className="space-y-2">
                      {col.items.map((item, j) => (
                        <li key={j} className="flex items-start text-sm text-gray-500">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2.5 mt-1.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 04 — Quality Control */}
      <section className="py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-[1fr] items-start">
            
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">{t.s04.title}</h2>
              <div className="space-y-8">
                {t.s04.items.map((item, i) => (
                  <div key={i}>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-8 md:gap-10 items-start">
            {t.s05.certImages.map((img, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="h-24 w-24 flex items-center justify-center">
                  <img src={img} alt={t.s05.certs[i]} className="max-h-24 max-w-24 object-contain" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t.cta.title}</h2>
          <p className="text-gray-500 mb-8">{t.cta.subtitle}</p>
          <Link
            to="/contact"
            className="inline-block px-10 py-3 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded transition-colors"
            data-testid="capabilities-cta-btn"
          >
            {t.cta.button}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CapabilitiesPage;
